import { createListenerMiddleware } from '@reduxjs/toolkit'
import * as Analytics from 'analytics/analytics'
import { trackPageView } from 'analytics/snowplow/events'
import { getOrderId, orderIdStateIsLoaded, waitPageViewDataLoaded } from '../analytics/snowplow/helpers'
import { waitForValue } from 'lib/promise/waitForValue'
import { isCheckoutReady } from 'checkout/selectors/view/generic'
import { PAGE_VIEW } from 'actions/actionConstants'
// Create instance
export const analyticsListenerMiddleware = createListenerMiddleware()

const pageChangedBetweenWait = (oldState:App.State, newState:App.State) => {
  return oldState.routeHistory.currentPath !== newState.routeHistory.currentPath
}

analyticsListenerMiddleware.startListening({
  type: PAGE_VIEW,
  effect: async(action, listenerApi) => {
    const state = listenerApi.getState() as App.State
    if (state.routeHistory.currentPath === state.routeHistory.prevPath) return
    // check conditions for awaiting specific redux state data items
    const numberOfItemsWaitedFor = await waitPageViewDataLoaded(state, listenerApi)
    const newState = listenerApi.getState() as App.State
    // if page changed between wait abandon tracking, the new page view will also trigger and with the correct data
    if (numberOfItemsWaitedFor.length > 0 && pageChangedBetweenWait(state, newState)) return
    Analytics.trackEvent(trackPageView(state.routeHistory.currentPath, state.routeHistory.currentPathParams))
  },
})

const waitForCheckoutReady = waitForValue<boolean>()
export const checkoutStateIsReady = () => waitForCheckoutReady.awaitForValue()
analyticsListenerMiddleware.startListening({
  // Run on any change as checkout can be affected by many items in state
  predicate: (_, currentState: App.State, previousState: App.State) => {
    const readyNow = isCheckoutReady(currentState)
    const readyBefore = isCheckoutReady(previousState)
    return readyNow !== readyBefore
  },
  effect: async(_, listenerApi) => {
    const state = listenerApi.getState() as App.State
    if (isCheckoutReady(state)) {
      waitForCheckoutReady.resolveWithValue(true)
    } else {
      waitForCheckoutReady.resetAwaitedValue()
    }
  },
})

const waitForOrderReady = waitForValue<boolean>()
export const orderStateIsReady = () => waitForOrderReady.awaitForValue()
analyticsListenerMiddleware.startListening({
  // Run on any change only
  predicate: (_, currentState: App.State, previousState: App.State) => {
    const orderNow = getOrderId(currentState)
    const orderBefore = getOrderId(previousState)
    return orderNow !== orderBefore
  },
  effect: async(_, listenerApi) => {
    const state = listenerApi.getState() as App.State
    const orderId = getOrderId(state)
    if (orderId && orderIdStateIsLoaded(state, orderId)) {
      waitForOrderReady.resolveWithValue(true)
    } else {
      waitForOrderReady.resetAwaitedValue()
    }
  },
})
